import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import baseURL from '../../config';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  Button,
  CircularProgress,
} from '@mui/material';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  maxWidth: 600,
  overflow: 'hidden',
  bgcolor: 'background.paper',
  border: '2px solid #1976d2',
  boxShadow: 24,
  p: 4,
};

const VesselContracts = () => {
  const { id: vesselownerId } = useParams();
  const [contracts, setContracts] = useState([]);
  const [contractCount, setContractCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalData, setModalData] = useState({ title: '', files: [] });

  const handleOpenModal = (title, files) => {
    setModalData({ title, files });
  };

  const handleCloseModal = () => {
    setModalData({ title: '', files: [] });
  };

  const getFileLink = (fileObj) => {
    if (!fileObj?.filePath) return null;
    return {
      url: `${baseURL}/${fileObj.filePath.replace(/\\/g, '/')}`,
      name: fileObj.originalName,
    };
  };

  const formatLabel = (label) =>
    label.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');

  const extractFiles = (sourceObj) =>
    Object.entries(sourceObj || {})
      .filter(([_, val]) => val?.filePath && val?.originalName)
      .map(([key, val]) => ({
        label: formatLabel(key),
        link: getFileLink(val),
      }));

  useEffect(() => {
    const fetchContracts = async () => {
      if (!vesselownerId) {
        setError('Vessel Owner ID is missing.');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Token is missing.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${baseURL}/api/vesselOwner/getVesselOwnerContractsByVesselOwnerId/${vesselownerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response?.data) {
          setContracts(response.data?.contracts || []);
          setContractCount(response.data?.count || 0);
        } else {
          setError('No contracts found.');
        }
      } catch (err) {
        console.error('Error fetching contracts:', err);
        setError('Failed to fetch contract data.');
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [vesselownerId]);

  const renderModal = () => {
    if (!modalData.title) return null;

    return (
      <Modal open={true} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={3} textAlign="center">
            {modalData.title}
          </Typography>

          {modalData.files.length > 0 ? (
            modalData.files.map(({ label, link }, idx) =>
              link ? (
                <Box
                  key={idx}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bgcolor="#fff"
                  boxShadow={2}
                  borderRadius={2}
                  px={2}
                  py={1.5}
                  mb={2}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
                      alt="PDF Icon"
                      width={24}
                      height={24}
                    />
                    <Typography fontWeight="500">{label}</Typography>
                  </Box>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="contained" size="small" color="primary">
                      Download
                    </Button>
                  </a>
                </Box>
              ) : null
            )
          ) : (
            <Typography>No documents available.</Typography>
          )}

          <Box textAlign="center" mt={3}>
            <Button variant="contained" color="error" onClick={handleCloseModal}>
              CLOSE
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box p={2}>
      <Typography variant="h5" mb={2}>
        Vessel Contracts
      </Typography>
      <Typography variant="subtitle1" mb={2}>
        Total Contracts: {contractCount}
      </Typography>

      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#1976d2' }}>
            <TableCell sx={{ color: 'white' }}>Sr. No.</TableCell>
            <TableCell sx={{ color: 'white' }}>Vessel Info</TableCell>
            <TableCell sx={{ color: 'white' }}>Owner</TableCell>
            <TableCell sx={{ color: 'white' }}>Crew</TableCell>
            <TableCell sx={{ color: 'white' }}>Crewing Agent</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contracts.map((contract, index) => {
            const vessel = contract.ShipDetails || {};
            const crew = contract.crewdetails || {};
            const owner = contract.vesselOwner || {};
            const crewingAgentFile = contract.BSM; // This is a single file object, not a nested object

            const crewingAgentFiles = crewingAgentFile?.filePath
              ? [
                  {
                    label: crewingAgentFile.originalName,
                    link: getFileLink(crewingAgentFile),
                  },
                ]
              : [];

            return (
              <TableRow key={contract._id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <strong>Name:</strong> {vessel.vesselname || 'N/A'}<br />
                  <strong>IMO:</strong> {vessel.IMO_Number || 'N/A'}<br />
                  <Button
                    variant="contained"
                    sx={{ mt: 1 }}
                    onClick={() => handleOpenModal('Vessel Files', extractFiles(vessel))}
                  >
                    Vessel Files
                  </Button>
                </TableCell>
                <TableCell>{owner?.company_shortname || 'N/A'}</TableCell>
                <TableCell>
                  <strong>Name:</strong> {crew?.name || 'N/A'}<br />
                  <strong>Rank:</strong> {crew?.rankname || 'N/A'}<br />
                  <Button
                    variant="contained"
                    sx={{ mt: 1, background: 'linear-gradient(to right, #8e2de2, #4a00e0)' }}
                    onClick={() => handleOpenModal('Crew Files', extractFiles(crew))}
                  >
                    Crew Files
                  </Button>
                </TableCell>
             <TableCell>
  <strong>Crewing Agent:</strong> {contract.crewingAgentName || 'N/A'}<br />
  {contract.BSM?.filePath && contract.BSM?.originalName ? (
    <Button
      variant="contained"
      color="primary"
      onClick={() =>
        handleOpenModal('Crewing Agent Documents', [
          {
            label: contract.BSM.originalName,
            link: getFileLink(contract.BSM),
          },
        ])
      }
    >
      View Documents
    </Button>
  ) : (
    'No Documents Available'
  )}
</TableCell>

              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {renderModal()}
    </Box>
  );
};

export default VesselContracts;
